<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useSlots, watch } from "vue";
import { useI18n } from "vue-i18n";
import type { ChatAgent, ChatMessage, ChatSurface, CompletionPart, ResourceLayout } from "../types/chat";
import type { AgentInputMode, AgentCompletionMode } from "../lib/agent-format";
import { isModalChatPresentation, type ResolvedChatPresentation } from "../lib/presentation";

/**
 * The expanded chat shell: a controlled, presentational surface.
 *
 * State (the conversation, `expanded`, streaming) lives in `<ChatExperience>`;
 * this component takes `messages`/`busy`/`agent` in and emits `send`/`cancel`/
 * `back` out. Two presentations:
 *
 *  - `takeover` (default): a full-screen modal that owns the viewport — locks
 *    background scroll and traps focus. Used on the candidate surface. It can
 *    pin the governing resource beside the thread (`split`) or inline.
 *  - `dock`: a right-hand side panel that sits *beside* the live page (the host
 *    layout reflows its content). It is non-modal — no scroll lock, no focus
 *    trap, no global Escape — so the page stays interactive and the user keeps
 *    context. The resource split/inline affordances are suppressed (the page
 *    itself is the context). Used on the admin surface.
 */
const props = withDefaults(
  defineProps<{
    agent?: ChatAgent | null;
    surface: ChatSurface;
    messages: ChatMessage[];
    busy?: boolean;
    /** Short label for the governing resource (e.g. the report name). */
    contextLabel?: string | null;
    /** Whether a resource governs this chat. Defaults to "is the resource slot filled?". */
    hasResource?: boolean;
    /** Whether free-text input is blocked (e.g. an interactive tool awaits an answer). */
    inputBlocked?: boolean;
    /** Hint shown when input is blocked. */
    inputHint?: string;
    /**
     * Candidate input affordance. `guided` hides the free-text composer so the user advances only by
     * answering the rendered question tools (a guided assessment); `freeText` (default) shows it.
     */
    inputMode?: AgentInputMode;
    /** Terminal completion payload (assessment finished), when the server has emitted one. */
    completion?: CompletionPart | null;
    /**
     * What to do on completion. `navigate` (default) means the host redirects away (no in-takeover
     * report). `report` keeps the user here and renders an in-chat report panel (host `#report` slot).
     */
    completionMode?: AgentCompletionMode;
    /** Whether terminal generic tool cards expose raw input/output payloads. */
    showToolDetails?: boolean;
    /** Draft text carried from the floating input to pre-fill the composer. */
    initialDraft?: string;
    /** Force a resource layout, overriding the agent preference. */
    layoutOverride?: ResourceLayout;
    /** How the shell is presented: full-screen `takeover` (default) or side `dock`. */
    presentation?: ResolvedChatPresentation;
  }>(),
  {
    agent: null,
    busy: false,
    contextLabel: null,
    hasResource: undefined,
    inputBlocked: false,
    inputHint: undefined,
    inputMode: "freeText",
    completion: null,
    completionMode: "navigate",
    showToolDetails: false,
    initialDraft: "",
    layoutOverride: undefined,
    presentation: "takeover",
  },
);

const emit = defineEmits<{
  (e: "send", text: string): void;
  (e: "cancel"): void;
  (e: "back"): void;
  (
    e: "submit-tool",
    payload: { invocationId: string; toolName: string; result: Record<string, unknown> },
  ): void;
  (
    e: "cancel-tool",
    payload: { invocationId: string; toolName: string },
  ): void;
  (
    e: "confirm-tool",
    payload: { invocationId: string; toolName: string; approve: boolean; summary?: string },
  ): void;
}>();

const slots = useSlots();
const isDock = computed(() => props.presentation === "dock");

// In-chat report: when the agent opts into `completion: "report"`, a finished assessment renders a
// report panel here (host-provided `#report` slot, with a built-in fallback) instead of redirecting.
const showCompletionReport = computed(
  () => props.completionMode === "report" && !!props.completion,
);
// The free-text composer is hidden in guided mode (advance only by answering tools) and once an
// in-chat report is shown (the assessment is over).
const showComposer = computed(() => props.inputMode !== "guided" && !showCompletionReport.value);
// A guided assessment with no composer still surfaces a thin status bar so the footer doesn't
// collapse and the user understands how to proceed.
const showGuidedFooter = computed(
  () => props.inputMode === "guided" && !showCompletionReport.value,
);
const guidedFooterText = computed(() =>
  props.inputBlocked && props.inputHint ? props.inputHint : t("guidedHint"),
);

// The dock only sits *beside* the page on wide viewports (>= xl / 1280px). Below
// that it expands to full width and must behave as a modal (focus trap, scroll
// lock, Escape, aria-modal) like the takeover — otherwise keyboard/scroll/screen
// readers could reach the page hidden behind it. `isWide` mirrors the same
// breakpoint as the panel width + the layout's content reflow.
const isWide = ref(false);
const isModal = computed(() => isModalChatPresentation(isDock.value ? "dock" : "takeover", isWide.value));

// The dock sits beside the live page, which *is* the working context — so the
// chat's own resource split/inline affordances are suppressed there.
const hasResourceEffective = computed(
  () => !isDock.value && (props.hasResource ?? !!slots.resource),
);

const { t } = useI18n({ useScope: "local" });

const resourceLabel = computed(() => props.contextLabel || t("resource"));

const bodyRef = ref<HTMLElement | null>(null);
const { showSplit, showInline } = useChatLayout(bodyRef, {
  agentLayout: () => props.agent?.layout,
  hasResource: () => hasResourceEffective.value,
  override: () => props.layoutOverride,
});

const inlineOpen = ref(false);

const roleLabel = computed(() =>
  props.agent?.source === "system" ? t("role.system") : t("role.context"),
);

const dialogLabel = computed(() => t("dialogLabel", { agent: props.agent?.name ?? t("assistant") }));

const rootRef = ref<HTMLElement | null>(null);
const composerRef = ref<{ focus: () => void } | null>(null);
let previouslyFocused: HTMLElement | null = null;
let previousBodyOverflow = "";

/** Visible, focusable elements within the takeover, in DOM order. */
function focusableElements(): HTMLElement[] {
  const root = rootRef.value;
  if (!root) {
    return [];
  }
  const selector =
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
  return Array.from(root.querySelectorAll<HTMLElement>(selector)).filter(
    // `getClientRects()` is empty for `display:none` elements (e.g. the split
    // aside on mobile) but correct for the `position:fixed` dialog subtree.
    (el) => el.getClientRects().length > 0 || el === document.activeElement,
  );
}

function onKeydown(event: KeyboardEvent) {
  // Escape returns to the page but never silently aborts an in-flight reply —
  // stopping is an explicit action via the Stop affordance.
  if (event.key === "Escape" && !props.busy) {
    event.preventDefault();
    emit("back");
    return;
  }

  // Trap Tab within the takeover so keyboard focus can't escape to the page
  // behind this modal surface.
  if (event.key === "Tab") {
    const focusables = focusableElements();
    if (focusables.length === 0) {
      return;
    }
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    const active = document.activeElement as HTMLElement | null;
    const outside = !rootRef.value?.contains(active);
    if (event.shiftKey) {
      if (active === first || outside) {
        event.preventDefault();
        last.focus();
      }
    } else if (active === last || outside) {
      event.preventDefault();
      first.focus();
    }
  }
}

// Modal chrome (focus trap, scroll lock, Escape→back) is applied whenever the
// shell behaves as a modal — the takeover always, the dock only while full-width
// (< xl). `applyModal` is idempotent and reactive so it stays correct if the
// viewport crosses the breakpoint while open.
let modalActive = false;
let mql: MediaQueryList | null = null;

function applyModal(active: boolean) {
  if (!import.meta.client) {
    return;
  }
  if (active && !modalActive) {
    // Capture phase so a child that stops propagation of Tab can't defeat the trap.
    window.addEventListener("keydown", onKeydown, { capture: true });
    // Lock background scroll while the modal owns the viewport (mobile especially).
    previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    modalActive = true;
  } else if (!active && modalActive) {
    window.removeEventListener("keydown", onKeydown, { capture: true });
    document.body.style.overflow = previousBodyOverflow;
    modalActive = false;
  }
}

function onMqlChange() {
  isWide.value = mql?.matches ?? true;
}

onMounted(async () => {
  previouslyFocused = (document.activeElement as HTMLElement) ?? null;
  mql = window.matchMedia("(min-width: 1280px)");
  isWide.value = mql.matches;
  mql.addEventListener("change", onMqlChange);
  applyModal(isModal.value);
  await nextTick();
  composerRef.value?.focus();
});

watch(isModal, (modal) => applyModal(modal));

onBeforeUnmount(() => {
  mql?.removeEventListener("change", onMqlChange);
  applyModal(false);
  // Fallback focus restore when used standalone; the parent restores to the
  // freshly rendered floating input when it owns the toggle (see experience.vue).
  previouslyFocused?.focus?.();
});
</script>

<template>
  <div
    ref="rootRef"
    :class="[
      'fixed flex flex-col bg-background',
      isDock
        ? 'inset-y-0 right-0 z-40 w-full border-l border-border shadow-xl xl:w-[var(--chat-dock-width,440px)]'
        : 'inset-0 z-50',
    ]"
    data-slot="chat-takeover"
    :data-presentation="presentation"
    :role="isModal ? 'dialog' : 'complementary'"
    :aria-modal="isModal ? 'true' : undefined"
    :aria-label="dialogLabel"
  >
    <header class="flex items-center gap-3 border-b border-border px-4 pb-3 pt-[calc(env(safe-area-inset-top)+0.75rem)]">
      <button
        v-if="!isDock"
        type="button"
        class="inline-flex min-h-9 items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        @click="emit('back')"
      >
        <Icon name="heroicons:arrow-left" mode="svg" class="size-4" />
        <span>{{ hasResourceEffective ? t("backToResource", { label: resourceLabel }) : t("back") }}</span>
      </button>

      <div class="flex min-w-0 items-center gap-2">
        <span
          class="grid size-7 shrink-0 place-items-center rounded-lg bg-primary/10 text-sm text-primary"
          aria-hidden="true"
        >
          <Icon v-if="agent?.icon" :name="agent.icon" mode="svg" class="size-4" />
          <template v-else>{{ agent?.avatar ?? agent?.name?.charAt(0)?.toUpperCase() ?? "✨" }}</template>
        </span>
        <div class="min-w-0 leading-tight">
          <p class="truncate text-sm font-semibold text-foreground">{{ agent?.name ?? t("assistant") }}</p>
          <p class="truncate text-xs text-muted-foreground">{{ roleLabel }}</p>
        </div>
      </div>

      <!-- Dock: a minimize control collapses back to the floating input. -->
      <button
        v-if="isDock"
        type="button"
        class="ml-auto inline-flex min-h-9 items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        :aria-label="t('minimize')"
        @click="emit('back')"
      >
        <Icon name="heroicons:chevron-double-right" mode="svg" class="size-4" />
        <span class="sr-only sm:not-sr-only">{{ t("minimize") }}</span>
      </button>

      <!-- Takeover: resource pin/inline affordances. -->
      <button
        v-else-if="showInline"
        type="button"
        class="ml-auto inline-flex min-h-9 items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        @click="inlineOpen = !inlineOpen"
      >
        <Icon name="heroicons:document-text" mode="svg" class="size-3.5" />
        {{ inlineOpen ? t("hideResource", { label: resourceLabel }) : t("viewResource", { label: resourceLabel }) }}
      </button>
      <span
        v-else-if="!showSplit"
        class="ml-auto inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground"
      >
        <Icon
          :name="hasResourceEffective ? 'heroicons:document-text' : 'heroicons:sparkles'"
          mode="svg"
          class="size-3.5"
        />
        {{ contextLabel || t("newResource") }}
      </span>
    </header>

    <div ref="bodyRef" class="flex min-h-0 flex-1">
      <section class="flex min-w-0 flex-1 flex-col">
        <div
          v-if="showInline && inlineOpen"
          class="mx-auto w-full max-w-3xl px-4 pt-4"
        >
          <div class="max-h-80 overflow-y-auto rounded-xl border border-border bg-card p-3">
            <slot name="resource" :compact="true" />
          </div>
        </div>

        <ChatThread
          :messages="messages"
          :agent="agent"
          :busy="busy"
          :surface="surface"
          :show-tool-details="showToolDetails"
          @submit-tool="(payload) => emit('submit-tool', payload)"
          @cancel-tool="(payload) => emit('cancel-tool', payload)"
          @confirm-tool="(payload) => emit('confirm-tool', payload)"
        />

        <ChatComposer
          v-if="showComposer"
          ref="composerRef"
          :agent-name="agent?.name"
          :busy="busy"
          :disabled="inputBlocked"
          :hint="inputBlocked ? inputHint : undefined"
          :initial-draft="initialDraft"
          @send="(text: string) => emit('send', text)"
          @cancel="emit('cancel')"
        />

        <div
          v-else-if="showCompletionReport"
          class="mx-auto w-full max-w-3xl px-4 py-4"
          data-slot="chat-completion-report"
        >
          <slot
            name="report"
            :completion="completion"
            :run-id="completion?.runId ?? null"
            :report-file="completion?.reportFile ?? null"
          >
            <div class="rounded-xl border border-primary/30 bg-primary/5 p-4">
              <p class="text-sm font-medium text-foreground">{{ t("complete.title") }}</p>
              <p class="mt-1 text-xs text-muted-foreground">
                {{ t("complete.runId", { id: completion?.runId || t("complete.unknownRun") }) }}
              </p>
            </div>
          </slot>
        </div>

        <p
          v-else-if="showGuidedFooter"
          class="mx-auto w-full max-w-3xl px-4 py-3 text-center text-xs text-muted-foreground"
          data-slot="chat-guided-hint"
        >
          {{ guidedFooterText }}
        </p>
      </section>

      <aside
        v-if="showSplit"
        class="hidden w-[420px] shrink-0 flex-col border-l border-border bg-card md:flex"
      >
        <div class="flex items-center gap-2 border-b border-border px-4 py-3">
          <Icon name="heroicons:document-text" mode="svg" class="size-4 text-primary" />
          <span class="truncate text-sm font-medium text-foreground">
            {{ contextLabel || t("resource") }}
          </span>
        </div>
        <div class="min-h-0 flex-1 overflow-y-auto p-4">
          <slot name="resource" :compact="false" />
        </div>
      </aside>
    </div>
  </div>
</template>

<i18n lang="yaml">
en:
  assistant: Assistant
  resource: resource
  newResource: New resource
  back: Back
  backToResource: Back to {label}
  minimize: Minimize
  viewResource: View {label}
  hideResource: Hide {label}
  dialogLabel: Chat with {agent}
  guidedHint: Answer the prompts above to continue.
  complete:
    title: Assessment complete
    runId: "Run ID: {id}"
    unknownRun: n/a
  role:
    system: system agent
    context: context agent
</i18n>
